// src/hooks/useAffiliateTracker.js
//
// Captura o código de indicação que chegou pela URL, conta o clique e guarda o
// código até o cadastro.
//
// O código precisa sobreviver a: landing → navegação → cadastro → OTP. Por isso
// localStorage, e não state: o fluxo passa por recarregamentos e o OTP leva o
// usuário para fora e de volta.
//
// PRIMEIRA ATRIBUIÇÃO VENCE. Se já existe um afiliado guardado, um segundo
// link não sobrescreve — a pessoa pertence a quem a trouxe primeiro, e o
// backend aplica a mesma regra ao consolidar (`AFILIADOS_INDICACOES/{uid}` é
// imutável depois de criado). Sem isto, bastaria mandar o próprio link a
// alguém já indicado para roubar a indicação alheia.

import { useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const CHAVE = 'affiliateData';
// Marca de clique já contado, por código. Recarregar a página e voltar à aba
// não são cliques novos — sem isto o afiliado veria um número inflado e sem
// relação com quantas pessoas realmente abriram o link dele.
const CHAVE_CLIQUE = 'lesvita:afiliado:clique:';

const normalizar = (valor) =>
  String(valor || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24);

/**
 * Conta 1 clique no link. Anônimo: sobe só o código.
 * Fire-and-forget — a landing nunca espera por isto.
 */
const contarClique = (codigo) => {
  try {
    if (sessionStorage.getItem(CHAVE_CLIQUE + codigo)) return;
    sessionStorage.setItem(CHAVE_CLIQUE + codigo, '1');
  } catch (e) {
    // Sem storage: no pior caso conta duas vezes. Métrica de vaidade não
    // justifica bloquear nada.
  }
  try {
    const corpo = JSON.stringify({ codigo, origem: 'link' });
    // `sendBeacon` sobrevive a quem clica e sai da página no mesmo segundo —
    // que é exatamente o visitante que um `fetch` normal perderia.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_BASE_URL}/afiliados/clique`,
        new Blob([corpo], { type: 'application/json' }),
      );
      return;
    }
    fetch(`${API_BASE_URL}/afiliados/clique`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: corpo,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* nada aqui pode atrapalhar a landing */
  }
};

const useAffiliateTracker = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // `ref` é o que importa: é o CÓDIGO, e o backend resolve o dono dele.
    // `aid` (uid do afiliado) ainda é LIDO por causa dos links antigos já
    // compartilhados, mas os links novos não o carregam mais — não havia
    // motivo para expor identificador interno em toda mensagem de WhatsApp.
    const codigo = normalizar(params.get('ref') || params.get('cupom'));
    if (!codigo) return;

    // O clique é contado ANTES da trava de primeira atribuição: quem já foi
    // indicado por outra pessoa e abre este link ainda é uma visita real, e o
    // afiliado precisa enxergá-la para entender a diferença entre alcance e
    // conversão.
    contarClique(codigo);

    // Primeira atribuição vence.
    if (localStorage.getItem(CHAVE)) return;

    try {
      localStorage.setItem(CHAVE, JSON.stringify({
        AFFILIATE_CODE: codigo,
        AFFILIATE_PARTNER_ID: params.get('aid') || null,
        AFFILIATE_ORIGIN: 'LINK',
        AFFILIATE_CAPTURED_AT: new Date().toISOString(),
      }));
    } catch (e) {
      // Navegador sem storage (aba privada, storage cheio): a indicação se
      // perde, mas o app segue normal. O cupom digitado no checkout continua
      // atribuindo a venda.
    }
  }, []);
};

export default useAffiliateTracker;
